/**
 * @fileoverview Bookmark searcher for saving and managing user bookmarks.
 * @module searchers/BookmarkSearcher
 */

import { IntSearcher } from './IntSearcher.js';
import { createIntSearcherConfig } from './IntSearcherConfigs.js';
import { SEARCH_TYPES, CLEAR_FIELDS, FIELD_ISBOOKMARK } from '../constants.js';


/**
 * Manages user bookmarks for places and people.
 * Provides persistence via localStorage and filtering/search functionality.
 * Bookmarks are stored with their full item data for map display.
 * @class
 * @extends IntSearcher
 */
export class BookmarkSearcher extends IntSearcher {
    /**
     * Creates a new BookmarkSearcher instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {Object} [options={}] - Configuration options
     */
    constructor(mapApp, options = {}) {
        const config = createIntSearcherConfig(SEARCH_TYPES.BOOKMARKS, options);
        super(mapApp, config);
    }






    // ==========================================
    // BOOKMARK HELPER METHODS
    // ==========================================
    // These methods provide bookmark-specific naming while delegating to
    // the generic CRUD methods in IntSearcher.


    /**
     * Checks if an item is bookmarked.
     * @param {string} uid - The unique identifier of the item to check
     * @returns {boolean} True if the item is bookmarked
     */
    isBookmarkedByUid(uid) {
        return this.hasItemByUid(uid);
    }

    /**
     * Toggles the bookmark state for the given uid.
     * @param {string} uid - The unique identifier of the item to toggle
     * @param {Object} item - The item to bookmark (used when adding)
     * @returns {boolean} True if the item is now bookmarked, false if unbookmarked
     */
    addOrRemoveBookmarkByUidItem(uid, item) {
        const debugMessage = 'addRemove bookmark';
        return this.addOrRemoveItemByUidItem(uid, item, debugMessage);
    }


    /**
     * Custom subclass version which also removes custom stuff
     * @param {string} uid - The unique identifier of the item to remove
     * @returns {boolean} True if removed successfully
     */
    removeItemByUidItem(uid, item, flagUpdateStorageAndRefresh) {
        if (item) {
            const fields = CLEAR_FIELDS[SEARCH_TYPES.BOOKMARKS];
            this.getMapApp().clearItemCustomFields(item, fields);
        }
        // call parent removeItemByUid
        return super.removeItemByUidItem(uid, item, flagUpdateStorageAndRefresh)
    }




    /**
     * Deserializes bookmark data and marks all items with the isBookmark flag.
     * @param {Array} serializedData - Array of serialized stub objects from localStorage
     * @returns {Array} Array of reconstituted bookmark items
     * @override
     */
    unSerializeData(serializedData) {
        // import
        const results = super.unSerializeData(serializedData);
        // mark as bookmarked
        for (const item of results) {
            item[FIELD_ISBOOKMARK] = true;
        }
        return results;
    }

    /**
     * Marks the item as a bookmark before adding it to results.
     * @param {Object} item - The item about to be added
     * @override
     */
    processItemBeforeAdd(item) {
        // base class does nothing
        item[FIELD_ISBOOKMARK] = true;
    }

}
